import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { parseDataRodadaIso } from 'helper/Relatorios/Dessem';
import { sendInterfaceErrorToSentry } from 'helper/sendErrorToSentry';
import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import {
  hidratarDessem,
  hojeComoDataAnalisada,
  ontemComoDataAnalisada,
  selectDessemHidratado,
} from 'redux/reducers/relatoriosDessemReducer';
import { useGetDessemOptionsQuery } from 'services/api/products/relatorios';
import { DessemCenario, DessemDataAnalisada } from 'types/Relatorios/Dessem';

const CENARIO_FALLBACK: DessemCenario = 'early';

/**
 * `default_data_rodada` (`'YYYY-MM-DD'`) -> `{ano, mes, dia}`. Ausente ou inválida
 * (ex.: `'2026-13-45'`, data-hora, outro formato) -> `fallback()` (padrão: hoje; o hook passa
 * o mesmo fallback do erro, que depende do perfil). O parse estrito (meia-noite local) é o
 * `parseDataRodadaIso` de `helper/Relatorios/Dessem`, compartilhado com o `useDessemMaxData`.
 */
export function parseDataRodada(
  iso: unknown,
  fallback: () => DessemDataAnalisada = hojeComoDataAnalisada,
): DessemDataAnalisada {
  const data = parseDataRodadaIso(iso);
  if (!data) return fallback();
  return { ano: data.getFullYear(), mes: data.getMonth() + 1, dia: data.getDate() };
}

/**
 * Data do Informe padrão quando o options não serve (erro ou data inválida): admin = hoje (o
 * informe que costuma estar em preparação); cliente = ontem (o último que costuma estar
 * publicado). Mesmo critério do `useDessemMaxData`.
 */
export const dataPadraoSemOptions = (isAdmin: boolean): (() => DessemDataAnalisada) => (
  isAdmin ? hojeComoDataAnalisada : ontemComoDataAnalisada
);

/**
 * Hidrata, UMA vez por sessão, a data/cenário iniciais do Informe DESSEM a partir do
 * `GET options` do backend: `default_data_rodada`, já como Data do Informe (D) (admin = D do
 * estudo mais recente no Core; cliente = último dia publicado, ontem se nenhum) e
 * `default_cenario` (`null` -> Early).
 * Os demais campos do options (`data_rodada`, `cenarios`, `core_*`) são ignorados aqui;
 * `max_data_rodada` é lido pelo `useDessemMaxData` (ControlPanel), que compartilha o mesmo
 * cache do RTK Query e o reconsulta periodicamente — sem efeito aqui: depois de `hidratado`
 * a consulta fica `skip`ada, o efeito sai cedo e `hidratarDessem` é no-op.
 *
 * - `ativo` (rota DESSEM com a product_key salva) e `hidratado` montam o `skip`: fora da
 *   rota, sem chave ou já hidratado, não consulta.
 * - Fail-open: se o options falhar, hidrata com a data padrão do perfil (admin hoje, cliente
 *   ontem; `dataPadraoSemOptions`) e Early, e registra um aviso no Sentry — a tela não fica
 *   presa no spinner por causa de um endpoint auxiliar.
 * - Idempotente: `hidratarDessem` é no-op depois de hidratado, e uma escolha do usuário no
 *   painel (`setDessem*`) vence uma resposta atrasada.
 * - Deps do efeito são primitivas (`dataRodada`, `cenario`, flags), não o objeto `data`.
 *
 * Devolve `hidratado`. O `RelatoriosLayout` mostra o spinner até ele virar `true`, para que
 * nenhuma seção (Outlet) nem o `DessemControlPanel` consulte o backend com a data provisória
 * — o `GET aprovacao` de admin cria o rascunho do dia pedido, e seria o dia errado.
 */
export default function useDessemHidratacao(ativo: boolean): boolean {
  const dispatch = useDispatch();
  const hidratado = useSelector(selectDessemHidratado);
  const isAdmin = Boolean(useSelector(selectUserIsAdmin));
  const { data, isError, error } = useGetDessemOptionsQuery(undefined, {
    skip: !ativo || hidratado,
  });

  const chegou = data !== undefined;
  const dataRodada = data?.default_data_rodada;
  const cenario = data?.default_cenario ?? null;

  useEffect(() => {
    if (!ativo || hidratado) return;

    if (chegou) {
      dispatch(hidratarDessem({
        dataAnalisada: parseDataRodada(dataRodada, dataPadraoSemOptions(isAdmin)),
        cenario: cenario ?? CENARIO_FALLBACK,
      }));
      return;
    }

    if (isError) {
      sendInterfaceErrorToSentry(
        'Informe DESSEM: options indisponível',
        'GET options falhou; a tela abriu com o fallback (data padrão do perfil, Early)',
        error,
        { level: 'warning', tags: { informe: 'dessem' } },
      );
      dispatch(hidratarDessem({
        dataAnalisada: dataPadraoSemOptions(isAdmin)(),
        cenario: CENARIO_FALLBACK,
      }));
    }
  }, [ativo, hidratado, chegou, dataRodada, cenario, isError, error, isAdmin, dispatch]);

  return hidratado;
}
